import React, { useEffect, useMemo } from 'react';
import Router from 'next/router';
import Head from 'next/head';
import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider } from '@mui/material/styles';
import { SnackbarProvider } from 'notistack';
import { DefaultSeo, DefaultSeoProps } from 'next-seo';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { initGA, logPageView } from '../../lib/google/analytics';
import { useApollo } from '../../lib/graphql/apollo';
import { DocumentNode } from '@apollo/client';
import { ApolloProvider } from '@apollo/client/react';
import elevatorTheme from '../../theme/elevatorTheme';
import { AuthProvider } from '../../hooks/useAuth';
import defaultSeo from './defaultSeo';
import { Theme } from '@mui/material/styles';
import type { StorageManager } from '@mui/system/cssVars';

type NotifyOptions = Record<string, unknown>;

interface ElevatorProviderProps {
  children: React.ReactNode;
  apiHost: string;
  reportError: (err: any, options: NotifyOptions) => void;
  initialApolloState?: any;
  googleAnalyticsTrackingCode?: string;
  defaultSeoProps?: DefaultSeoProps;
  theme?: Theme;
  /**
   * When the theme uses CSS variables, this storage keeps the mode on the
   * shared color-mode cookie instead of a separate localStorage key.
   */
  modeStorageKey?: string;
  defaultMode?: 'light' | 'dark' | 'system';
  storageManager?: StorageManager | null;
  userQuery?: DocumentNode;
  // Optional explicit endpoints (defaults to apiHost + '/api' and apiHost + '/auth')
  apiEndpoint?: string;
  authEndpoint?: string;
}

/**
 * Bundles functionality of:
 * - `DefaultSeo`
 * - `ThemeProvider`
 * - `SnackbarProvider`
 * - `LocalizationProvider`
 * - `ApolloProvider`
 * - `CSSBaseline`
 * - `AuthProvider`
 */
const ElevatorProvider = ({
  children,
  apiHost,
  reportError,
  initialApolloState = {},
  googleAnalyticsTrackingCode,
  defaultSeoProps,
  theme = elevatorTheme,
  modeStorageKey,
  defaultMode,
  storageManager,
  userQuery,
  apiEndpoint,
  authEndpoint,
}: ElevatorProviderProps) => {
  // Use explicit endpoints if provided, otherwise fall back to apiHost + path
  const graphqlEndpoint = apiEndpoint ?? `${apiHost}/api`;
  const authEndpointUrl = authEndpoint ?? `${apiHost}/auth`;

  const apolloClient = useApollo(graphqlEndpoint, initialApolloState);

  // Merge defaultSeoProps with defaultSeo to allow partial overrides
  const mergedSeoProps = useMemo(() => {
    if (!defaultSeoProps) {
      return defaultSeo;
    }

    return {
      ...defaultSeo,
      ...defaultSeoProps,
      // Deep merge nested objects
      openGraph: {
        ...defaultSeo.openGraph,
        ...defaultSeoProps.openGraph,
      },
      twitter: {
        ...defaultSeo.twitter,
        ...defaultSeoProps.twitter,
      },
    };
  }, [defaultSeoProps]);

  useEffect(() => {
    //Initialize GA4 if not already intialized and set that it is.
    if (
      !window.GA_ANALYTICS &&
      /*!apiHost.includes('localhost:') &&*/
      googleAnalyticsTrackingCode
    ) {
      initGA(googleAnalyticsTrackingCode);
      window.GA_ANALYTICS = true;

      //Log a pageview if google analytics is set and not local
      if (!apiHost.includes('localhost:')) {
        logPageView(window.location.pathname, document.title);
        Router.events.on('routeChangeComplete', logPageView);

        // Cleanup: remove event listener on unmount
        return () => {
          Router.events.off('routeChangeComplete', logPageView);
        };
      }
    }
  }, [apiHost, googleAnalyticsTrackingCode]);

  return (
    <>
      <Head>
        {/* Use minimum-scale=1 to enable GPU rasterization */}
        <meta
          name="viewport"
          content="minimum-scale=1, initial-scale=1, width=device-width, shrink-to-fit=no"
        />
        <meta httpEquiv="X-UA-Compatible" content="IE=edge" />
      </Head>
      <DefaultSeo {...mergedSeoProps} />
      <ThemeProvider
        theme={theme}
        modeStorageKey={modeStorageKey}
        defaultMode={defaultMode}
        storageManager={storageManager ?? undefined}
        disableTransitionOnChange
      >
        <SnackbarProvider
          maxSnack={3}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
        >
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <ApolloProvider client={apolloClient}>
              <CssBaseline />
              <AuthProvider
                reportError={reportError}
                authEndpoint={authEndpointUrl}
                userQuery={userQuery}
              >
                {children}
              </AuthProvider>
            </ApolloProvider>
          </LocalizationProvider>
        </SnackbarProvider>
      </ThemeProvider>
    </>
  );
};

export default ElevatorProvider;
